import { useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { UnderlineTabs } from "../../../components/ui/UnderlineTabs.jsx";
import { schemaTableCell, schemaTableHeader } from "../../../components/ui/schemaTableStyles.js";
import { buildAccountDetailTokens } from "../../Accounts/accountDetailTheme.js";
import {
  accountHistoryTone,
  formatAccountHistoryAction,
  formatBindingTimestamp,
  formatSimHistoryAction,
  simHistoryTone,
} from "./deviceBindingUtils.js";

const HISTORY_TABS = [
  { id: "account", label: "Account history" },
  { id: "sim", label: "SIM history" },
];

export function DeviceBindingHistorySection({ device }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const [historyTab, setHistoryTab] = useState("account");

  const accountHistory = device?.accountHistory ?? [];
  const simHistory = device?.simHistory ?? [];

  const activeEntries = historyTab === "account" ? accountHistory : simHistory;
  const emptyMessage =
    historyTab === "account"
      ? "No account assignments recorded yet."
      : "No SIM bindings recorded yet.";
  const renderLabel = historyTab === "account" ? formatAccountHistoryAction : formatSimHistoryAction;
  const toneFor = historyTab === "account" ? accountHistoryTone : simHistoryTone;

  return (
    <div style={{ background: ap.card, border: `1px solid ${ap.cardBorder}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <UnderlineTabs tabs={HISTORY_TABS} activeTab={historyTab} onChange={setHistoryTab} paddingX={14} />
      <HistoryTable
        entries={activeEntries}
        emptyMessage={emptyMessage}
        renderLabel={renderLabel}
        toneFor={toneFor}
        ap={ap}
        c={c}
        A={A}
      />
    </div>
  );
}

function HistoryTable({ entries, emptyMessage, renderLabel, toneFor, ap, c, A }) {
  return (
    <div style={{ overflow: "auto", flex: 1 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: c.hover }}>
            <th style={schemaTableHeader(c, ap)}>Event</th>
            <th style={schemaTableHeader(c, ap, { width: 180 })}>Date</th>
            <th style={schemaTableHeader(c, ap, { width: 120 })}>Status</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={3} style={{ ...schemaTableCell(c), color: c.sub, fontSize: 12, padding: "18px 14px" }}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            entries.map((entry) => (
              <HistoryTableRow
                key={entry.id}
                entry={entry}
                label={renderLabel(entry)}
                tone={toneFor(entry.action)}
                c={c}
                A={A}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTableRow({ entry, label, tone, c, A }) {
  const palette = tonePalette(tone, A, c);

  return (
    <tr
      onMouseEnter={(e) => (e.currentTarget.style.background = c.hover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={{ transition: "background .15s ease" }}
    >
      <td style={schemaTableCell(c, { verticalAlign: "middle" })}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: c.text, lineHeight: 1.4 }}>{label}</span>
      </td>
      <td style={{ ...schemaTableCell(c, { verticalAlign: "middle" }), color: c.sub, fontSize: 11.5, whiteSpace: "nowrap" }}>
        {formatBindingTimestamp(entry.createdAt)}
      </td>
      <td style={schemaTableCell(c, { verticalAlign: "middle" })}>
        <span
          style={{
            display: "inline-block",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: palette.badgeText,
            background: palette.badgeBg,
            border: `1px solid ${palette.badgeBorder}`,
            borderRadius: 999,
            padding: "2px 7px",
          }}
        >
          {entry.action}
        </span>
      </td>
    </tr>
  );
}

function tonePalette(tone, A, c) {
  if (tone === "positive") {
    return { badgeBg: `${A}14`, badgeBorder: `${A}30`, badgeText: A };
  }
  if (tone === "info") {
    return { badgeBg: "#3B82F614", badgeBorder: "#3B82F630", badgeText: "#3B82F6" };
  }
  return { badgeBg: c.hover, badgeBorder: c.cardBdr, badgeText: c.mid };
}
